import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { APP_INACTIVITY_MINUTES } from "../utils/constants";
import { useDispatch } from "react-redux";
import { logoutUser } from "../../../apis/accounts/auth";
import { AppDispatch } from "../../../store/store";

const IdleTimer = ({
  children,
  isAuthenticated,
}: {
  children: React.ReactNode;
  isAuthenticated: boolean;
}) => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);

  // Events that determine inactivity
  const events = useRef([
    "load",
    "mousemove",
    "mousedown",
    "click",
    "scroll",
    "keypress",
    "touchstart",
    "touchmove",
  ]);

  const inactivityTimeout = APP_INACTIVITY_MINUTES * 60 * 1000;

  // Format milliseconds to minutes:seconds for logging
  const formatTime = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const logTimeLeft = useCallback(() => {
    if (startTimeRef.current) {
      const elapsed = Date.now() - startTimeRef.current;
      // const timeLeft = Math.max(0, inactivityTimeout - elapsed);
      // console.log(`🕒 IdleTimer: ${formatTime(timeLeft)} until logout`);
    }
  }, [inactivityTimeout]);

  const startCountdownLogging = useCallback(() => {
    // Clear existing countdown
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    startTimeRef.current = Date.now();

    // Log initial time
    // console.log(
    //   `🎯 IdleTimer: Timer started/reset. Time until logout: ${formatTime(
    //     inactivityTimeout
    //   )}`
    // );

    // Log time left every 30 seconds
    countdownRef.current = setInterval(() => {
      logTimeLeft();
    }, 30000);
  }, [inactivityTimeout, logTimeLeft]);

  const logoutAction = useCallback(() => {
    // console.log("🚪 IdleTimer: Logging out due to inactivity");

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    dispatch(logoutUser());
    navigate("/sign-in");
  }, [dispatch, navigate]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleLogoutTimer = useCallback(() => {
    resetTimer();

    // Start the countdown logging
    startCountdownLogging();

    // Set the actual logout timer
    timerRef.current = setTimeout(() => {
      // console.log("⏰ IdleTimer: Timeout reached - executing logout");
      logoutAction();
    }, inactivityTimeout);
  }, [inactivityTimeout, logoutAction, resetTimer, startCountdownLogging]);

  const initTimer = useCallback(() => {
    // console.log("🔔 IdleTimer: Activity detected - resetting timer");
    handleLogoutTimer();
  }, [handleLogoutTimer]);

  useEffect(() => {
    if (!isAuthenticated) {
      // console.log("🔒 IdleTimer: User not authenticated - cleaning up timers");
      resetTimer();
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      return;
    }

    // console.log("🔑 IdleTimer: User authenticated - starting idle timer");

    // Initialize the timer when authenticated
    handleLogoutTimer();

    // Add event listeners
    events.current.forEach((event) => {
      window.addEventListener(event, initTimer);
    });

    return () => {
      // console.log("🧹 IdleTimer: Cleaning up event listeners and timers");
      resetTimer();
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      events.current.forEach((event) => {
        window.removeEventListener(event, initTimer);
      });
    };
  }, [isAuthenticated, handleLogoutTimer, initTimer, resetTimer]);

  return <>{children}</>;
};

export default IdleTimer;
// import { useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import { APP_INACTIVITY_MINUTES } from "../utils/constants";
// import { useDispatch } from "react-redux";
// import { logoutUser } from "../../../apis/accounts/auth";
// import { AppDispatch } from "../../../store/store";

// const IdleTimer = ({
//   children,
//   isAuthenticated,
// }: {
//   children: React.ReactNode;
//   isAuthenticated: boolean;
// }) => {
//   //Get RememberMe state from User
//   const rememberUser = localStorage.getItem("rememberMe");
//   const navigate = useNavigate();
//   const dispatch: AppDispatch = useDispatch();

//   // Eevents that determine In-Activity
//   const events = [
//     "load",
//     "mousemove",
//     "mousedown",
//     "click",
//     "scroll",
//     "keypress",
//   ];

//   let timer: any = null;

//   const inactivityTimeout = APP_INACTIVITY_MINUTES * 60 * 1000;

//   const resetTimer = () => {
//     clearTimeout(timer);
//     timer = null;
//   };

//   const logoutAction = () => {
//     dispatch(logoutUser());
//     navigate("/sign-in");
//   };

//   const handleLogoutTimer = () => {
//     timer = setTimeout(() => {
//       resetTimer();
//       events.forEach((item) => window.removeEventListener(item, initTimer));
//       logoutAction(); // Log out the user
//     }, inactivityTimeout);
//   };

//   useEffect(() => {
//     if (isAuthenticated) {
//       handleLogoutTimer();
//       events.forEach((event) => window.addEventListener(event, initTimer));
//     }

//     return () => {
//       resetTimer();
//       events.forEach((event) => window.removeEventListener(event, initTimer));
//     };
//   }, [isAuthenticated]);

//   const initTimer = () => {
//     resetTimer();
//     handleLogoutTimer();
//   };

//   return children;
// };

// export default IdleTimer;
